import { useEffect, useRef, useState } from 'react';
import { searchAddress } from '../../api/addressApi';
import { MapPickerOverlay } from './MapPicker.styles';
import * as googleAdapter from './adapters/googleAdapter';
import * as kakaoAdapter from './adapters/kakaoAdapter';

// 지도 provider 토글 아이콘 (KR=태극기, GLOBAL=지구본)
const FLAG_KR = (
    <svg viewBox="0 0 100 100" width="14" height="14">
        <circle cx="50" cy="50" r="48" fill="#fff" stroke="#dee2e6" strokeWidth="2" />
        <g transform="rotate(35 50 50)">
            <path d="M2 50 A48 48 0 0 1 98 50 Z" fill="#cd2e3a" />
            <path d="M2 50 A48 48 0 0 0 98 50 Z" fill="#0047a0" />
            <circle cx="26" cy="50" r="24" fill="#cd2e3a" />
            <circle cx="74" cy="50" r="24" fill="#0047a0" />
        </g>
    </svg>
);
const GLOBE = (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.7">
        <circle cx="12" cy="12" r="9" />
        <ellipse cx="12" cy="12" rx="4" ry="9" />
        <path d="M3 12h18M4.5 8h15M4.5 16h15" />
    </svg>
);

const MapPicker = ({ show, lat, lon, provider: initialProvider = 'google', onSelect, onClose }) => {
    const handleRef = useRef(null);
    const onSelectRef = useRef(onSelect);
    const [searchResults, setSearchResults] = useState(null);
    const [provider, setProvider] = useState(initialProvider);

    onSelectRef.current = onSelect;

    // 지도를 열 때마다 현재 위치 기준 provider로 초기화 (이후 토글로 변경 가능)
    useEffect(() => {
        if (show) setProvider(initialProvider);
    }, [show, initialProvider]);

    useEffect(() => {
        if (!show || !lat || !lon) return;

        let cancelled = false;
        let initLat = lat;
        let initLon = lon;
        const adapter = provider === 'kakao' ? kakaoAdapter : googleAdapter;

        let timer = setTimeout(async () => {
            let container = document.getElementById("mapPicker");
            if (!container) return;
            try {
                await adapter.load();
            } catch (e) {
                console.error("지도 SDK 로드 실패:", e);
                return;
            }
            if (cancelled) return;
            container.innerHTML = ""; // provider 전환/재열림 시 이전 지도 DOM 정리
            handleRef.current = adapter.createPicker(container, { lat: initLat, lon: initLon }, {
                onSelect: (la, lo) => { if (onSelectRef.current) onSelectRef.current(la, lo); },
                onMove: () => { }
            });
        }, 50);

        return () => {
            cancelled = true;
            clearTimeout(timer);
            if (handleRef.current && handleRef.current.destroy) handleRef.current.destroy();
            handleRef.current = null;
        };
    }, [show, lat, lon, provider]);

    const changeProvider = (p) => {
        if (p === provider) return;
        setSearchResults(null);
        setProvider(p);
    };

    const handleClose = () => {
        setSearchResults(null);
        if (onClose) onClose();
    };

    const handleSearch = (e) => {
        if (e.key === 'Enter' && e.target.value.trim()) {
            searchAddress(e.target.value.trim(), (results) => {
                setSearchResults(results);
            }, provider);
        }
    };

    const handleResultClick = (item) => {
        let newLat = parseFloat(item.lat);
        let newLon = parseFloat(item.lon);
        setSearchResults(null);
        if (handleRef.current) handleRef.current.recenter(newLat, newLon);
    };

    if (!show) return null;

    return (
        <MapPickerOverlay>
            <div className='mapWrap'>
                <div className='mapSearch'>
                    <input type="text" placeholder="장소 검색" onKeyDown={handleSearch} />
                    {searchResults && searchResults.length > 0 ?
                        <ul className='searchResultList'>
                            {searchResults.map((item, i) => (
                                <li key={i} onClick={() => handleResultClick(item)}>
                                    {item.display_name}
                                </li>
                            ))}
                        </ul>
                        : null}
                </div>
                <div id="mapPicker"></div>
                {/* 검색 결과 목록이 좌측 상단을 덮으므로, 목록이 떠 있는 동안 토글은 숨김 */}
                {!(searchResults && searchResults.length > 0) &&
                    <div className='mapRegionToggle'>
                        <button type="button" className={provider === 'kakao' ? 'active' : ''} onClick={() => changeProvider('kakao')}>
                            {FLAG_KR}<span>KR</span>
                        </button>
                        <button type="button" className={provider === 'google' ? 'active' : ''} onClick={() => changeProvider('google')}>
                            {GLOBE}<span>GLOBAL</span>
                        </button>
                    </div>}
                <svg className="closeIcon" onClick={handleClose} width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <line x1="6" y1="6" x2="18" y2="18" stroke="#495057" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="18" y1="6" x2="6" y2="18" stroke="#495057" strokeWidth="2" strokeLinecap="round"/>
                </svg>
            </div>
        </MapPickerOverlay>
    );
};

export default MapPicker;
